import { CA_VALIDATION_STATUSES } from '@/services/data/LookupDataService';
import { CAFilters } from '@/types/model/corrective-action';
import { PageHeaderFilterControl, PageHeaderFilterOption } from '@/types/model/pageHeaderBar';

const CA_STATUS_OPTIONS: Array<{ id: number; name: string }> = [
  { id: 0, name: 'Issue In Progress' },
  { id: 1, name: 'Response Pending' },
  { id: 2, name: 'Acceptance Pending' },
  { id: 3, name: 'Accepted Open' },
  { id: 4, name: 'Accepted Closed' },
  { id: 5, name: 'Response in Progress' },
  { id: 6, name: 'Rescinded Closed' },
];

const truncate = (value: string, maxLength: number = 36): string => {
  if (value.length <= maxLength) {
    return value;
  }

  return `${value.slice(0, Math.max(0, maxLength - 3))}...`;
};

const getStatusText = (selectedIds: number[]): string => {
  const selectedText = CA_STATUS_OPTIONS
    .filter((option) => selectedIds.includes(option.id))
    .map((option) => option.name)
    .join(', ');

  return selectedText.length > 0 ? truncate(selectedText) : 'None';
};

const getValidationText = (selectedIds: number[]): string => {
  const selectedText = CA_VALIDATION_STATUSES
    .filter((status) => selectedIds.includes(status.Id))
    .map((status) => status.Name || '(Not Set)')
    .join(', ');

  return selectedText.length > 0 ? truncate(selectedText) : 'None';
};

export const buildCorrectiveActionFilterControls = (
  filters: CAFilters
): PageHeaderFilterControl<number>[] => {
  const statusFilterOptions: PageHeaderFilterOption<number>[] = CA_STATUS_OPTIONS.map((status) => ({
    label: status.name,
    value: status.id,
  }));

  const validationFilterOptions: PageHeaderFilterOption<number>[] = CA_VALIDATION_STATUSES.map((status) => ({
    label: status.Name || '(Not Set)',
    value: status.Id,
  }));

  return [
    {
      type: 'toggle',
      id: 'corrective-actions-show-all-filter',
      label: 'Show All',
      checked: filters.showAll,
    },
    {
      type: 'select',
      id: 'corrective-actions-status-filter',
      label: 'Statuses',
      value: filters.selectedStatusIds,
      options: statusFilterOptions,
      multiple: true,
      placeholder: 'Select statuses',
      selectedText: getStatusText(filters.selectedStatusIds),
      disabledWhen: {
        controlId: 'corrective-actions-show-all-filter',
        checked: true,
      },
    },
    {
      type: 'select',
      id: 'corrective-actions-validation-filter',
      label: 'Validation',
      value: filters.selectedValidationStatusIds,
      options: validationFilterOptions,
      multiple: true,
      placeholder: 'Select validation status',
      selectedText: getValidationText(filters.selectedValidationStatusIds),
      disabledWhen: {
        controlId: 'corrective-actions-show-all-filter',
        checked: true,
      },
    },
  ];
};

const getToggleValue = (controls: PageHeaderFilterControl<number>[], controlId: string): boolean | null => {
  const control = controls.find((candidate) => candidate.id === controlId);
  if (!control || control.type !== 'toggle') {
    return null;
  }

  return control.checked;
};

const getSelectedValues = (controls: PageHeaderFilterControl<number>[], controlId: string): number[] => {
  const control = controls.find((candidate) => candidate.id === controlId);
  if (!control || control.type !== 'select') {
    return [];
  }

  return Array.isArray(control.value) ? control.value : [control.value];
};

export const mapCorrectiveActionControlsToFilters = (
  controls: PageHeaderFilterControl<number>[],
  currentFilters: CAFilters
): CAFilters => {
  const showAll = getToggleValue(controls, 'corrective-actions-show-all-filter') ?? currentFilters.showAll;

  if (showAll) {
    return {
      ...currentFilters,
      showAll: true,
      selectedStatusIds: CA_STATUS_OPTIONS.map((status) => status.id),
      selectedValidationStatusIds: CA_VALIDATION_STATUSES.map((status) => status.Id),
    };
  }

  return {
    ...currentFilters,
    showAll: false,
    selectedStatusIds: getSelectedValues(controls, 'corrective-actions-status-filter'),
    selectedValidationStatusIds: getSelectedValues(controls, 'corrective-actions-validation-filter'),
  };
};

export const buildCorrectiveActionFilterSummary = (filters: CAFilters): string => {
  if (filters.showAll) {
    return 'All';
  }

  return `Statuses: ${getStatusText(filters.selectedStatusIds)} | Validation: ${getValidationText(filters.selectedValidationStatusIds)}`;
};
